import React, { useState, useMemo, useEffect, useContext } from 'react';
import { X as CloseIcon } from 'react-feather';
import type { CSSObjectWithLabel, ControlProps, IndicatorsContainerProps, ValueContainerProps, InputProps, DropdownIndicatorProps, ClearIndicatorProps, MenuProps, MultiValueProps } from 'react-select';
import Select, { components } from 'react-select';
import styled from '@emotion/styled';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { Checkbox } from '../presentation';
import { useChartFilters, useChartFilterDispatch } from './Chat.Chart.Filter.Context';

interface ThemingContextType {
  enabled: boolean;
  palette: {
    primary: {
      main: string;
    };
  };
}

interface SelectOption {
  value: string;
  label: string;
}

interface ChartDataPoint {
  label?: string;
  x?: string;
  value?: number;
  y?: number;
}

interface ChartInfo {
  chart?: {
    type?: string;
  };
  quantItemId?: number;
  title?: string;
  filters?: FilterRow[];
}

const MenuRoot = styled.div({
  width: '100%',
  minWidth: 420,
  maxWidth: 500,
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
});

const MenuHeader = styled.div(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  padding: '0 0 8px 0',
}));

const AnalysisTabSection = styled.div({
  padding: '0 0 12px 0',
  fontSize: 14,
  fontWeight: 500,
  color: '#222',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

const AnalysisTabValue = styled.span({
  fontWeight: 400,
  color: '#444',
  marginLeft: 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 200,
  display: 'inline-block',
});

const MenuContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const FilterRowContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 4,
});

const StyledSelect = styled(Select)<{ width?: number }>(({ width }) => ({
  minWidth: width || 120,
  fontSize: 14,
  '& .react-select__multi-value': {
    display: 'none !important',
    backgroundColor: 'transparent !important',
  },
  '& .react-select__multi-value__label': {
    display: 'none !important',
  },
  '& .react-select__multi-value__remove': {
    display: 'none !important',
  },
  '& .react-select__option': {
    backgroundColor: 'transparent !important',
    color: 'inherit !important',
    '&:hover': {
      backgroundColor: 'var(--gray-xl) !important',
    },
    '&--is-selected': {
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
    },
    '&--is-focused': {
      backgroundColor: 'var(--gray-xl) !important',
      color: 'inherit !important',
    },
  },
}));

const RemoveButton = styled.button({
  background: 'none',
  border: 'none',
  padding: 6,
  marginLeft: 8,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '18px',
  color: 'var(--gray-d)',
  '&:hover': {
    backgroundColor: 'var(--gray-xl)',
  },
});

const AddFilterRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginTop: 8,
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '5px',
});

const AddFilterText = styled.span<{ appThemingContext: ThemingContextType | null }>(({ appThemingContext }) => ({
  color: appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
    ? appThemingContext.palette.primary.main
    : 'var(--black)',
  fontFamily: 'var(--font-semibold)',
  fontSize: 15,
  userSelect: 'none',
  padding: '4px 8px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
      ? `${appThemingContext.palette.primary.main}20` // 20% opacity
      : 'var(--blue-xl)',
    color: appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
      ? appThemingContext.palette.primary.main
      : 'var(--pri-02)',
  },
}));

const buildSelectStyles = (appThemingContext: ThemingContextType | null) => ({
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    minHeight: 32,
    height: 'auto', // Allow height to expand based on content
    borderRadius: 5,
    borderColor: 'var(--gray-l)',
    boxShadow: 'none',
    fontSize: 14,
    '&:hover': {
      borderColor: 'var(--gray-m)',
    },
    '&:focus-within': {
      borderColor: 'var(--gray-m)',
      outline: 'none',
    },
  } as CSSObjectWithLabel),
  indicatorsContainer: (provided: CSSObjectWithLabel) => ({
    ...provided,
    height: 32,
  } as CSSObjectWithLabel),
  valueContainer: (provided: CSSObjectWithLabel) => ({
    ...provided,
    minHeight: 32,
    height: 'auto', // Allow height to expand
    padding: '6px 8px',
  } as CSSObjectWithLabel),
  input: (provided: CSSObjectWithLabel) => ({
    ...provided,
    margin: 0,
    padding: 0,
  } as CSSObjectWithLabel),
  dropdownIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
  } as CSSObjectWithLabel),
  clearIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: 4,
  } as CSSObjectWithLabel),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: 5,
    border: '1px solid var(--gray-l)',
  } as CSSObjectWithLabel),
  multiValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: 'none !important', // Force hide individual value containers completely
    visibility: 'hidden',
    opacity: 0,
    width: 0,
    height: 0,
    margin: 0,
    padding: 0,
  } as CSSObjectWithLabel),
  multiValueLabel: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: 'none !important', // Force hide individual value labels completely
    visibility: 'hidden',
    opacity: 0,
  } as CSSObjectWithLabel),
  multiValueRemove: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: 'none !important',
    visibility: 'hidden',
    opacity: 0,
  } as CSSObjectWithLabel),
  option: (provided: CSSObjectWithLabel, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...provided,
    backgroundColor: 'transparent !important',
    color: 'inherit',
    '&:hover': {
      backgroundColor: appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
        ? `${appThemingContext.palette.primary.main}20 !important` // Add 20% opacity
        : 'var(--gray-xl) !important',
    },
    '&:active': {
      backgroundColor: 'transparent !important',
    },
    '&:focus': {
      backgroundColor: 'transparent !important',
    },
  } as CSSObjectWithLabel),
});
// --- End Styled Components ---

type FilterRow = {
  id: number;
  selectedOption: SelectOption | null;
  valueOption: SelectOption[];
};

const selectOptions: SelectOption[] = [
  { value: 'selected', label: 'Selected' },
  { value: 'not_selected', label: 'Not Selected' },
];

type ChartFilterMenuProps = {
  chartInfo: ChartInfo;
  originalData: ChartDataPoint[];
};

export function ChartFilterMenu({ chartInfo, originalData }: ChartFilterMenuProps) {
  const appThemingContext = useContext(AppThemingContext);

  // Create select styles with theming context
  const selectStyles = useMemo(() => buildSelectStyles(appThemingContext), [appThemingContext]);

  // Composite key for chartId
  const chartType = chartInfo?.chart?.type || 'unknown';
  const chartId = `${chartInfo?.quantItemId || 0}_${chartType}`;
  const dispatch = useChartFilterDispatch();

  const allFilters = useChartFilters();
  const contextFilters = useMemo(() => {
     
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filters = (allFilters as any)[chartId];
    return (filters && Array.isArray(filters) ? filters : []) as FilterRow[];
  }, [chartId, allFilters]);

  const valueOptions = useMemo(() => {
    if (!originalData || !Array.isArray(originalData)) return [];

    // Determine chart type
    const chartType = chartInfo?.chart?.type;

    let labels: string[] = [];

    if (chartType === 'pie' || chartType === 'donut') {
      // Pie/Donut: use 'label'
      labels = originalData
        .filter((dataPoint: ChartDataPoint): dataPoint is ChartDataPoint & { label: string } =>
          typeof dataPoint.label === 'string')
        .map(dataPoint => dataPoint.label);
    } else if (chartType === 'bar' || chartType === 'stacked-bar') {
      // Bar/Stacked-bar: use 'x'
      labels = originalData
        .filter((dataPoint: ChartDataPoint): dataPoint is ChartDataPoint & { x: string } =>
          typeof dataPoint.x === 'string')
        .map(dataPoint => dataPoint.x);
    } else {
      // Fallback: try both
      labels = originalData
        .map((dataPoint: ChartDataPoint) => dataPoint.label ?? dataPoint.x)
        .filter((v: string | undefined): v is string => typeof v === 'string');
    }

    const uniqueLabels = Array.from(new Set(labels));
    return uniqueLabels.map((label: string): SelectOption => ({ value: label, label }));
  }, [originalData, chartInfo]);

  const [filters, setFilters] = useState<FilterRow[]>(
    contextFilters && Array.isArray(contextFilters) && contextFilters.length > 0
      ? contextFilters
      : [{ id: 1, selectedOption: selectOptions[0], valueOption: [] }]
  );

  useEffect(() => {
    if (contextFilters && Array.isArray(contextFilters) && contextFilters.length > 0) {
      setFilters(contextFilters);
    }
  }, [contextFilters]);

  const handleAddFilter = () => {
    setFilters(prev => {
      const updated = [
        ...prev,
        { id: Date.now(), selectedOption: selectOptions[0], valueOption: [] },
      ];
      dispatch({ type: 'UPDATE_FILTERS', chartId, filters: updated });
      return updated;
    });
  };

  const handleRemoveFilter = (id: number) => {
    setFilters(prev => {
      const updated = prev.length > 1 ? prev.filter(f => f.id !== id) : prev;
      dispatch({ type: 'UPDATE_FILTERS', chartId, filters: updated });
      return updated;
    });
  };

  const handleSelectChange = (id: number, selected: SelectOption | null) => {
    setFilters(prev => {
      const updated = prev.map(f => (f.id === id ? { ...f, selectedOption: selected } : f));
      dispatch({ type: 'UPDATE_FILTERS', chartId, filters: updated });
      return updated;
    });
  };

  const handleValueChange = (id: number, value: SelectOption[] | null) => {
    setFilters(prev => {
      const updated = prev.map(f => (f.id === id ? { ...f, valueOption: value || [] } : f));
      dispatch({ type: 'UPDATE_FILTERS', chartId, filters: updated });
      return updated;
    });
  };

  // Custom MenuList for checkboxes and Select All / Clear All
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function MenuList(props: any) {
     
    const { options, selectProps } = props;
     
    const allSelected = selectProps.value.length === options.length;
    return (
      <div>
        <div style={{ display: 'flex', gap: 12, padding: '4px 12px', borderBottom: '1px solid var(--gray-l)' }}>
          <span
            style={{ color: 'var(--black)', cursor: 'pointer', fontSize: 13 }}
            onMouseDown={e => {
              e.preventDefault();
               
               
              selectProps.onChange([...options], { action: 'select-option' });
            }}>
            Select All
          </span>
          <span
            style={{ color: 'var(--black)', cursor: 'pointer', fontSize: 13 }}
            onMouseDown={e => {
              e.preventDefault();
               
              selectProps.onChange([], { action: 'deselect-option' });
            }}>
            Clear All
          </span>
        </div>
        <components.MenuList {...props}>
          { }
          {props.children}
        </components.MenuList>
      </div>
    );
  }  // Custom Option to show checkboxes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function Option(props: any) {
    // const color = appThemingContext?.enabled ? appThemingContext?.palette?.primary?.main : undefined;

    return (
      <components.Option {...props}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Checkbox
            checked={props.isSelected}
            onChange={() => { }} />
          <span>{props.label}</span>
        </div>
      </components.Option>
    );
  }
  // Custom ValueContainer to display selected values as comma-separated text
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function ValueContainer(props: any) {
     
    const { selectProps, children } = props;
     
    const { value, placeholder } = selectProps;

    // Only show comma-separated text for multi-select with values
    if (Array.isArray(value) && value.length > 0) {
      const displayText = value.map((v: SelectOption) => v.label).join(', ');

      return (
        <components.ValueContainer {...props}>
          <div style={{
            color: 'inherit',
            fontSize: 14,
            overflow: 'visible',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '16px',
          }}>
            {displayText}
          </div>
          {/* Keep the input for typing/searching */}
          {React.Children.toArray(children as React.ReactNode[]).filter((child: React.ReactNode) =>
            React.isValidElement(child) &&
            child.type &&
             
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (child.type as any).componentName === 'Input'
          )}
        </components.ValueContainer>
      );
    }

    // Default behavior for empty state
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  return (
    <MenuRoot>
      <MenuHeader>Filters</MenuHeader>
      <AnalysisTabSection>
        Analyses Tab: <AnalysisTabValue>{chartInfo?.title || '...'}</AnalysisTabValue>
      </AnalysisTabSection>
      <MenuContent>
        {filters.map((filter, idx) => (
          <FilterRowContainer key={filter.id}>
            <StyledSelect
              options={selectOptions}
              value={filter.selectedOption}
              onChange={option => handleSelectChange(filter.id, option as { value: string; label: string } | null)}
              isSearchable={false}
              menuPlacement="auto"
              styles={selectStyles}
              width={120} />
            <StyledSelect
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={valueOptions}
              value={filter.valueOption}
              onChange={option => handleValueChange(filter.id, option as SelectOption[] | null)}
              placeholder="Select a value"
              isSearchable={false}
              menuPlacement="auto"
              styles={selectStyles}
              width={250}
              components={{ MenuList, Option, ValueContainer }} />
            {filters.length > 1 && (
              <RemoveButton
                type="button"
                aria-label="Remove filter"
                onClick={() => handleRemoveFilter(filter.id)}>
                <CloseIcon size={18} color="#222" />
              </RemoveButton>
            )}
          </FilterRowContainer>
        ))}
        <AddFilterRow onClick={handleAddFilter}>
          <AddFilterText appThemingContext={appThemingContext}>+ Add Filter</AddFilterText>
        </AddFilterRow>
      </MenuContent>
    </MenuRoot>
  );
}